import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const COMMANDS = [
  { id: 'new-ticket',    label: 'New Ticket',      shortcut: 'N', path: '/tickets/new', section: 'Actions' },
  { id: 'dashboard',     label: 'Dashboard',       shortcut: 'D', path: '/dashboard', section: 'Navigate' },
  { id: 'tickets',       label: 'Tickets',         shortcut: 'T', path: '/tickets', section: 'Navigate' },
  { id: 'knowledge',     label: 'Knowledge Base',  shortcut: 'K', path: '/knowledge-base', section: 'Navigate' },
  { id: 'chat',          label: 'Live Chat',       shortcut: 'C', path: '/live-chat', section: 'Navigate' },
  { id: 'settings',      label: 'Settings',        shortcut: 'S', path: '/settings', section: 'Navigate' },
  { id: 'profile',       label: 'My Profile',      shortcut: 'P', path: '/profile', section: 'Navigate' },
  { id: 'users',         label: 'Users',           shortcut: 'U', path: '/users', section: 'Navigate' },
  { id: 'reports',       label: 'Reports',         shortcut: 'R', path: '/reports', section: 'Navigate' },
  { id: 'templates',     label: 'Ticket Templates',           path: '/ticket-templates', section: 'Navigate' },
  { id: 'webhooks',      label: 'Webhooks',                   path: '/webhooks', section: 'Navigate' },
  { id: 'audit',         label: 'Audit Logs',                 path: '/audit-logs', section: 'Navigate' },
];

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const filtered = COMMANDS.filter((c) =>
    c.label.toLowerCase().includes(query.toLowerCase())
  );

  const run = useCallback((cmd) => {
    setOpen(false);
    setQuery('');
    navigate(cmd.path);
  }, [navigate]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) { inputRef.current?.focus(); setActiveIdx(0); }
  }, [open]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && filtered[activeIdx]) { run(filtered[activeIdx]); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onClick={() => setOpen(false)}>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a command..."
            className="flex-1 bg-transparent outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400"
          />
          <kbd className="text-[10px] font-mono bg-gray-100 dark:bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        <div className="max-h-[320px] overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No results found.</p>
          ) : (
            filtered.map((cmd, i) => (
              <button
                key={cmd.id}
                onClick={() => run(cmd)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors ${
                  i === activeIdx ? 'bg-primary/10 text-primary' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{cmd.section}</span>
                  <p className="text-sm font-medium">{cmd.label}</p>
                </div>
                {cmd.shortcut && (
                  <kbd className="text-[10px] font-mono bg-gray-100 dark:bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">
                    {cmd.shortcut}
                  </kbd>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
